import { ComponentProps, PropsWithChildren, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { useUniqueId } from '~/utils/compute';
import IconBase from './Icon';

function CardSectionRoot({
  children,
  className,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
}: PropsWithChildren<{
  className?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
}>) {
  return (
    <section
      className={twMerge(
        'grid w-full auto-rows-auto items-center gap-4 sm:gap-y-0',
        // columns //
        'grid-cols-1',
        'has-[.media]:grid-cols-[3.5rem_1fr]',
        'has-[.action]:sm:grid-cols-[1fr_15rem]',
        'sm:has-[.media]:has-[.action]:grid-cols-[3.5rem_1fr_15rem]',
        // areas //
        'has-[.media]:[grid-template-areas:"media"]',
        'has-[.media]:has-[.header]:[grid-template-areas:"media_header"]',
        'has-[.media]:has-[.header]:has-[.action]:[grid-template-areas:"media_header"_"._action"] sm:has-[.media]:has-[.header]:has-[.action]:[grid-template-areas:"media_header_action"]',
        'has-[.media]:has-[.content]:[grid-template-areas:"media_content"]',
        'has-[.media]:has-[.content]:has-[.action]:[grid-template-areas:"media_content"_"._action"] sm:has-[.media]:has-[.content]:has-[.action]:[grid-template-areas:"media_content_action"]',
        'has-[.media]:has-[.header]:has-[.content]:[grid-template-areas:"media_header"_"._content"]',
        'has-[.media]:has-[.header]:has-[.content]:has-[.action]:[grid-template-areas:"media_header"_"._content"_"._action"] sm:has-[.media]:has-[.header]:has-[.content]:has-[.action]:[grid-template-areas:"media_header_action"_"._content_."]',
        'has-[.header]:[grid-template-areas:"header"]',
        'has-[.header]:has-[.action]:[grid-template-areas:"header"_"action"] sm:has-[.header]:has-[.action]:[grid-template-areas:"header_action"]',
        'has-[.header]:has-[.content]:has-[.action]:[grid-template-areas:"header"_"content"_"action"] sm:has-[.header]:has-[.content]:has-[.action]:[grid-template-areas:"header_action"_"content_."]',
        'has-[.content]:[grid-template-areas:"content"]',
        'has-[.content]:has-[.action]:[grid-template-areas:"content"_"action"] sm:has-[.content]:has-[.action]:[grid-template-areas:"content_action"]',
        className,
      )}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      {children}
    </section>
  );
}

function CardSectionHeader({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <header className={twMerge('header [grid-area:header]', className)}>
      {children}
    </header>
  );
}

function CardSectionTitle({
  id,
  children,
  className,
}: PropsWithChildren<{ id?: string; className?: string }>) {
  return (
    <div
      id={id}
      className={twMerge('subheading-md text-on-surface', className)}
    >
      {children}
    </div>
  );
}

function CardSectionDescription({
  id,
  children,
  className,
}: PropsWithChildren<{ id?: string; className?: string }>) {
  return (
    <div
      id={id}
      className={twMerge('body-md text-on-surface-variant', className)}
    >
      {children}
    </div>
  );
}

function CardSectionContent({
  className,
  children,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={twMerge('content [grid-area:content]', className)}>
      {children}
    </div>
  );
}

function CardSectionMedia({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <figure
      className={twMerge(
        'media flex justify-center justify-self-center text-on-surface [grid-area:media]',
        className,
      )}
    >
      {children}
    </figure>
  );
}

function CardSectionIcon({
  icon,
  className,
}: Pick<ComponentProps<typeof IconBase>, 'icon' | 'className'>) {
  return <IconBase icon={icon} className={twMerge('size-8', className)} />;
}

function CardSectionAction({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={twMerge(
        'action grid grid-cols-1 [grid-area:action] *:w-full',
        className,
      )}
    >
      {children}
    </div>
  );
}

type SectionProps = Omit<
  ComponentProps<typeof CardSectionRoot>,
  'ariaLabelledBy' | 'ariaDescribedBy' | 'ariaLabel'
> & {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
};

type SectionPropsWithIcon = SectionProps & {
  icon: ComponentProps<typeof IconBase>['icon'];
  media?: never;
};

type SectionPropsWithMedia = SectionProps & {
  media: ReactNode;
  icon?: never;
};

type CardSectionProps =
  | SectionProps
  | SectionPropsWithIcon
  | SectionPropsWithMedia;

function CardSection({
  title,
  description,
  action,
  className,
  children,
  ...props
}: CardSectionProps) {
  const labelledby = useUniqueId();
  const describedby = useUniqueId();

  const hasIcon = 'icon' in props && props.icon;
  const hasMedia = 'media' in props && props.media;
  const hasFigure = hasIcon || hasMedia;
  const needsHeader = !!title || !!description;

  return (
    <CardSectionRoot
      className={className}
      ariaLabelledBy={title ? labelledby : undefined}
      ariaDescribedBy={description ? describedby : undefined}
    >
      {hasFigure && (
        <CardSectionMedia>
          {hasIcon && <CardSectionIcon icon={props.icon} />}
          {hasMedia && props.media}
        </CardSectionMedia>
      )}
      {needsHeader && (
        <CardSectionHeader>
          {title && (
            <CardSectionTitle id={labelledby}>{title}</CardSectionTitle>
          )}
          {description && (
            <CardSectionDescription id={describedby}>
              {description}
            </CardSectionDescription>
          )}
        </CardSectionHeader>
      )}
      {children && <CardSectionContent>{children}</CardSectionContent>}
      {action && <CardSectionAction>{action}</CardSectionAction>}
    </CardSectionRoot>
  );
}

export default CardSection;
